// src/components/Sidebar.tsx
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Define user roles enum for better type safety and consistency
enum Roles {
    AGENT = 'AGENT',
    MANAGER = 'MANAGER',
    ADMIN = 'ADMIN',
    SUPER_ADMIN = 'SUPER_ADMIN',
}

// Define interface for navigation items
interface NavItem {
    label: string;
    icon: string;
    route: string;
    roles: Roles[];
}

const Sidebar: React.FC = () => {
    const [expanded, setExpanded] = useState<boolean>(false);
    const { userRoles } = useAuth(); // Get userRoles from AuthContext

    // Ensure userRoles is an array, even if it's empty or undefined
    const roles: string[] = Array.isArray(userRoles) ? userRoles : [];

    // Updated navItems with all possible roles for each route
    const navItems: NavItem[] = [
        {
            label: 'Dashboard',
            icon: '/icons/dashboard-grey.png',
            route: '/dashboard',
            roles: [Roles.AGENT, Roles.MANAGER, Roles.ADMIN, Roles.SUPER_ADMIN],
        },
        {
            label: 'Access Manager',
            icon: '/icons/sb-grey.png',
            route: '/access-manager',
            roles: [Roles.AGENT, Roles.MANAGER, Roles.ADMIN, Roles.SUPER_ADMIN],
        },
        {
            label: 'Agentic Workflow',
            icon: '/icons/campaign-grey.png',
            route: '/agentic-workflow',
            roles: [Roles.AGENT, Roles.MANAGER, Roles.ADMIN, Roles.SUPER_ADMIN],
        },
        {
            label: 'Contact Manager',
            icon: '/icons/leads-grey.png',
            route: '/contact-manager',
            roles: [Roles.AGENT, Roles.MANAGER, Roles.ADMIN, Roles.SUPER_ADMIN],
        },
        {
            label: 'Agent Command Center',
            icon: '/icons/agents-grey.png',
            route: '/agent-command-center',
            roles: [Roles.AGENT],
        },
        {
            label: 'Manager Command Center',
            icon: '/icons/call-studio-grey.png',
            route: '/manager-command-center',
            roles: [Roles.MANAGER, Roles.ADMIN, Roles.SUPER_ADMIN],
        },
        {
            label: 'Super Admin Data Entry',
            icon: '/icons/settings-grey.png',
            route: '/super-admin-data-entry',
            roles: [Roles.SUPER_ADMIN],
        },
        {
            label: 'Phone Number Manager',
            icon: '/icons/api-grey.png',
            route: '/phone-number-manager',
            roles: [Roles.SUPER_ADMIN],
        },
        // {
        //     label: 'SMS Management',
        //     icon: '/icons/sms-inbox-grey.png',
        //     route: '/sms-management',
        //     roles: [Roles.AGENT, Roles.MANAGER],
        // }
        // {
        //     label: 'SLAI Training Portal',
        //     icon: '/icons/slai-training-grey.png',
        //     route: '/slai-training',
        //     roles: [Roles.SUPER_ADMIN, Roles.ADMIN]
        // },
    ];

    const toggleSidebar = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.stopPropagation();
        setExpanded(!expanded);
    };

    // If we have no roles or empty array, show all items for development
    // Remove this in production or customize as needed
    const shouldShowAllItems: boolean = roles.length === 0;

    const filteredNavItems: NavItem[] = shouldShowAllItems
        ? navItems
        : navItems.filter((item: NavItem) => {
              // Check if any of the user's roles match with the required roles for this menu item
              const userRoleStrings: string[] = roles.map((role) =>
                  typeof role === 'string' ? role.toUpperCase() : String(role)
              );

              const hasAccess: boolean = item.roles.some((itemRole: Roles) => userRoleStrings.includes(itemRole));
              return hasAccess;
          });

    return (
        <aside className={`sidebar ${expanded ? 'expanded' : ''}`}>
            <div className='sidebar__logo'>
                <img src='/SLAILOGO.png' alt='SLAI Logo' className='sidebar__logo-img' />
            </div>
            <nav className='sidebar__nav'>
                {filteredNavItems.map((item: NavItem) => (
                    <NavLink
                        key={item.label}
                        to={item.route}
                        className={({ isActive }: { isActive: boolean }) =>
                            `sidebar__nav-item ${isActive ? 'active' : ''}`
                        }
                        data-label={item.label}
                    >
                        <img src={item.icon} alt={`${item.label} Icon`} className='sidebar__nav-icon' />
                        <span className='sidebar__nav-text'>{item.label}</span>
                    </NavLink>
                ))}
            </nav>
            <div className='sidebar__toggle'>
                <button id='sidebar-toggle-btn' className={expanded ? 'expanded' : ''} onClick={toggleSidebar}>
                    <img src='/icons/arrow.png' alt='Toggle Sidebar' />
                </button>
            </div>

            {/* Debug info - now visible during development */}
            <div
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    padding: '5px',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    fontSize: '10px',
                    borderRadius: '0 5px 0 0',
                }}
            >
                Roles: {roles.join(', ') || 'None (showing all items)'}
            </div>
        </aside>
    );
};

export default Sidebar;
